import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Crosshair, FileCode, BarChart3, Cloud, Boxes, Gauge } from "lucide-react";
import { MaskedLines, FadeUp, Eyebrow, EASE, SectionHead } from "@/components/Reveal";
import DemoForm from "@/components/DemoForm";
import ProductSchema from "@/components/ProductSchema";

const HERO_IMG =
  "https://images.pexels.com/photos/10410019/pexels-photo-10410019.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHIPS = ["10 mm H / 15 mm V", '10.1" Android 14', "iSite cloud", "Engcon / Steelwrist"];

const FEATURES = [
  { icon: Crosshair, title: "Real-time 3D guidance", body: "Bucket-tip position computed live against the design model, so the operator digs the drawing — not a guess." },
  { icon: FileCode, title: "Advanced CAD engine", body: "Opens LandXML, DWG and DXF directly, plus .dc, .cal, .jxl, .lok and .loc coordinate files." },
  { icon: BarChart3, title: "Color cut/fill maps", body: "2D and 3D views with color-coded cut/fill and customizable operator shortcuts on a 1920×1200 display." },
  { icon: Cloud, title: "iSite cloud", body: "Over-the-air updates, remote troubleshooting and data synchronization keep downtime near zero." },
  { icon: Boxes, title: "Tiltrotator ready", body: "Automated tilt control for Engcon and Steelwrist tilt-rotators, tilt buckets and standard buckets." },
  { icon: Gauge, title: "Precision stack", body: "MC300 GNSS at 8 mm + 1 ppm horizontal, IS300 100 Hz IMU at ±0.05°, dual IP68 AT315 antennas." },
];

const COMPARE = [
  { k: "Guidance", a: "Depth & slope from a field reference", b: "Full 3D model in the cab" },
  { k: "Design files", a: "Not required — field-defined", b: "DWG · DXF · LandXML · .jxl/.cal" },
  { k: "Accuracy", a: "±30 mm", b: "10 mm H / 15 mm V" },
  { k: "Display", a: '10.1" · 1024×600', b: '10.1" · 1920×1200 · Android 14' },
  { k: "Cloud", a: "—", b: "iSite cloud · OTA · remote support" },
  { k: "Attachments", a: "Tilt buckets · swing/triple/offset booms", b: "+ Engcon & Steelwrist tilt-rotators" },
  { k: "Best for", a: "Trenching, utilities, slope trimming", b: "Roads, foundations, mass excavation" },
];

const SPECS = [
  { k: "System accuracy", v: "10 mm horizontal / 15 mm vertical (manufacturer rating)" },
  { k: "MC300 GNSS receiver", v: "RTK 8 mm + 1 ppm H · 15 mm + 1 ppm V · IP67" },
  { k: "IS300 IMU sensor", v: "100 Hz · ±0.05° static / ±0.1° dynamic · IP69K" },
  { k: "AT315 antennas", v: "Dual GNSS · IP68" },
  { k: "MCPad300 display", v: '10.1" · 1920×1200 · Android 14 · 8-core 2.2 GHz · 6 GB / 64 GB · IP67' },
  { k: "Connectivity", v: "4G LTE · Wi-Fi · Bluetooth · 410–470 MHz radio modem · CAN · RS232" },
  { k: "Design formats", v: "LandXML · DWG · DXF · .dc · .cal · .jxl · .lok · .loc" },
  { k: "Compatibility", v: "Standard buckets · tilt buckets · Engcon & Steelwrist tilt-rotators" },
];

const PRODUCT_SCHEMA = {
  "@type": "Product",
  name: "CHCNAV MCNAV TX73 — 3D Excavator Machine Control",
  image: HERO_IMG,
  description:
    "3D excavator machine control with 10 mm horizontal accuracy, 100 Hz IMU and a 10.1-inch Android display. Runs LandXML, DWG and DXF files, tilt-ready with Engcon and Steelwrist. Supplied and supported across Australia by Green Grade.",
  sku: "MCNAV-TX73",
  model: "MCNAV TX73",
  category: "Construction machine control",
  brand: { "@type": "Brand", name: "CHCNAV" },
  manufacturer: { "@type": "Organization", name: "CHCNAV" },
  url: "https://greengrade.au/products/3d",
};

export default function Product3D() {
  return (
    <main>
      <ProductSchema product={PRODUCT_SCHEMA} />
      <section className="relative flex min-h-[88svh] items-end overflow-hidden" data-testid="product-3d-hero">
        <motion.div
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0"
        >
          <img src={HERO_IMG} alt="Excavator working at sunset" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/70" />
        </motion.div>
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto w-full max-w-[1600px] px-4 pb-20 pt-32 sm:px-8 lg:px-16">
          <FadeUp y={12}>
            <Eyebrow>
              <span>
                <Link to="/precision-solutions" data-testid="p3d-eyebrow-chcnav-link" className="underline decoration-neon/40 underline-offset-4 transition-colors hover:text-white">
                  CHCNAV
                </Link>{" "}
                MCNAV TX73
              </span>
            </Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.15}
            lines={["3D machine", { text: "control.", lineClass: "italic text-neon" }]}
            className="mt-4 font-display text-6xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-8xl lg:text-9xl"
          />
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Model-driven excavation with a 10.1-inch CAD display, dual GNSS and a 100 Hz IMU. The bucket follows the
            drawing — 10 mm horizontal, 15 mm vertical.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <span key={c} className="border border-signal/40 bg-ink/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon backdrop-blur-sm">
                {c}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="product-3d-demo-cta"
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
            >
              Request a demo
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/#quote"
              data-testid="product-3d-quote-cta"
              className="inline-flex items-center gap-3 border border-signal/50 px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-neon transition-colors hover:bg-signal/10"
            >
              Build a quote
            </Link>
            <Link
              to="/products/rover"
              data-testid="product-3d-to-rover-link"
              className="inline-flex items-center gap-3 border border-line px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:border-signal hover:text-neon"
            >
              Pair with a rover
            </Link>
            <Link
              to="/products/2.5d"
              data-testid="product-3d-to-25d-link"
              className="inline-flex items-center gap-3 border border-line px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:border-signal hover:text-neon"
            >
              Compare with 2.5D
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-3d-features">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="What it does" title="The drawing, in the cab" body="TX73 turns your design model into live bucket guidance — from highways and slopes to foundations and trenches." />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.06}>
                <div className="h-full border border-line bg-surface p-6 transition-colors duration-300 hover:border-signal/50 sm:p-8">
                  <f.icon className="h-6 w-6 text-signal" />
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight text-white">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-fog">{f.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="product-3d-compare">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Deciding" title="2.5D or 3D?" body="Both retrofit to your fleet. The question is whether your jobsites hand you design models." />
          <FadeUp delay={0.12} className="mt-12">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-fog lg:hidden" data-testid="compare-3d-swipe-hint">
              ← Swipe the table sideways →
            </p>
            <div data-testid="compare-table" className="overflow-x-auto border border-line">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="border-b border-line bg-surface">
                    <th className="px-5 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-fog">System</th>
                    <th className="px-5 py-4 font-display text-lg font-bold uppercase text-white">
                      <Link to="/products/2.5d" data-testid="compare-emg100-link" className="underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">EasyNAV EMG100</Link> · 2.5D
                    </th>
                    <th className="bg-signal/10 px-5 py-4 font-display text-lg font-bold uppercase text-neon">MCNAV TX73 · 3D</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((r, i) => (
                    <tr key={r.k} className={i % 2 === 0 ? "bg-surface/50" : ""}>
                      <td className="border-b border-line px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-fog">{r.k}</td>
                      <td className="border-b border-line px-5 py-4 text-sm text-slate-300">{r.a}</td>
                      <td className="border-b border-line bg-signal/5 px-5 py-4 text-sm text-neon">{r.b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeUp>
          <FadeUp delay={0.18}>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-fog">
              Starting out? The same TX73 hardware also runs a{" "}
              <Link to="/hire" data-testid="compare-2d-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">
                2D software tier
              </Link>{" "}
              — benchmark-led depth and slope, upgradeable to this full 3D at any time. Weighing up all three?{" "}
              <Link to="/compare" data-testid="compare-full-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">
                See the full 2D vs 2.5D vs 3D breakdown
              </Link>
              .
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-3d-specs">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Technical" title="Specifications" />
          <div className="mt-12">
            {SPECS.map((s, i) => (
              <FadeUp key={s.k} delay={i * 0.04} y={16}>
                <div className="flex flex-col gap-1 border-b border-line py-5 sm:flex-row sm:items-baseline sm:justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">{s.k}</span>
                  <span className="max-w-[60%] text-sm text-white sm:text-right sm:text-base">{s.v}</span>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.1} className="mt-10">
            <Link
              to="/specifications#tx73"
              data-testid="product-3d-spec-link"
              className="inline-flex items-center gap-2 border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:border-signal hover:text-neon"
            >
              Full specification breakdown <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-3d-form">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Get it on your machine" title="Request a TX73 demo" body="Bring us a design file and a machine — we'll show you the bucket following the model." />
          <FadeUp delay={0.12} className="mt-12">
            <DemoForm />
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
